import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { SubcriptionApis } from '../Api/SubcriptionService';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const UnsubcribePage = () => {
    const location = useLocation();
    const hasCalled = useRef(false);
    const queryParams = new URLSearchParams(location.search);

    const token = queryParams.get("oauth");
    const InvNum = queryParams.get("invNum");
    const amount = queryParams.get("amount");
    const pageType = queryParams.get("type");
    const payableAmount = queryParams.get("PA");
    const companyToken = queryParams.get("CTID");
    const invoiceToken = queryParams.get("INID");
    const stripeToken = queryParams.get("STID");
    const CEmail = queryParams.get("CUE");

    const [clientSecret, setClientSecret] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const oneTimeCheckout = async (email, amounts, InvNums) => {
        const payload = {
            currency: "usd",
            amount: Number(amounts),
            customerEmail: email,
            invoiceNumber: InvNums,
        };
        const apiCall = SubcriptionApis.createOneTimeCheckout(payload, token);
        toast.promise(apiCall, {
            loading: "Preparing payment...",
            error: "Failed to create payment session,try again"
        });
        const res = await apiCall;
        const clientSecret = res?.data?.clientSecret;
        if (!clientSecret) {
            throw new Error("Client secret not returned");
        }
        setClientSecret(clientSecret);
    };
    const callPaymentApi = async (amount, stripeId) => {
        const payload = {
            stripeAccountId: stripeId,
            currency: "usd",
            amount: Number(amount),
            customerEmail: CEmail,
            invoiceId: invoiceToken,
            companyId: companyToken
        };
        const apiCall = SubcriptionApis.payAmountForInvoice(payload);
        toast.promise(apiCall, {
            loading: "Preparing payment...",
            error: "Failed to create payment session,try again"
        });
        const res = await apiCall;
        const clientSecret = res?.data?.clientSecret;
        if (!clientSecret) {
            throw new Error("Client secret not returned");
        }
        setClientSecret(clientSecret);
    };
    const getUserDetail = async () => {
        if (!token) {
            setError(true);
            setLoading(false);
            return;
        }
        try {
            const userRes = await SubcriptionApis.userProfile(token);
            const email = userRes?.data?.email;
            if (!email) {
                throw new Error("User email not found");
            }
            const [amountRes, invNumRes] = await Promise.all([
                SubcriptionApis.decodeTokenByValue(amount),
                SubcriptionApis.decodeTokenByValue(InvNum)
            ]);
            await oneTimeCheckout(email, amountRes.data.data, invNumRes.data.data);
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to load user details"
            );
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    const decryptedAmount = async () => {
        if (!payableAmount) {
            setError(true);
            setLoading(false);
            return;
        }
        try {
            const [userRes, stripeIS] = await Promise.all([
                SubcriptionApis.decodeTokenByValue(payableAmount),
                SubcriptionApis.decodeTokenByValue(stripeToken)
            ]);
            await callPaymentApi(userRes.data.data, stripeIS.data.data);
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to load amount details"
            );
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (hasCalled.current) return;
        if (pageType === "Enterprise") {
            hasCalled.current = true;
            getUserDetail();
        }
        if (pageType === "INV") {
            hasCalled.current = true;
            decryptedAmount();
        }
    }, []);


    return (
        <div
            className={`min-h-screen section-padding flex items-center justify-center ${(!loading && !error && clientSecret) ? '' : 'section-dark'}`}
            style={(!loading && !error && clientSecret) ? { background: "#eaeaea" } : {}}
        >
            <div className="container max-w-2xl mx-auto text-center">

                {loading && (
                    <div className="py-20">
                        <div className="w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto"></div>
                        <p className="mt-4">Loading payment page...</p>
                    </div>
                )}

                {!loading && error && (
                    <div className="py-20">
                        <h2 className="text-xl font-semibold mb-2">
                            Failed to load payment page
                        </h2>

                        <button
                            onClick={() => window.location.reload()}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {!loading && !error && clientSecret && (
                    <div className="py-10">
                        <EmbeddedCheckoutProvider
                            stripe={stripePromise}
                            options={{
                                clientSecret
                            }}
                        >
                            <EmbeddedCheckout />
                        </EmbeddedCheckoutProvider>
                    </div>
                )}

            </div>
        </div>
    );
};

export default UnsubcribePage;
